import { apiClient, ensureCsrfCookie } from "./client";
import type { AuthenticatedUser } from "./types";

export async function loginWithBackend(email: string, password: string) {
    const response = await apiClient.post<{ message: string; user: AuthenticatedUser }>("/auth/login", {
        email,
        password,
    });

    return response.data;
}

export async function registerWithBackend(name: string, email: string, password: string, passwordConfirmation: string) {
    const response = await apiClient.post<{ message: string; user: AuthenticatedUser }>("/auth/register", {
        name,
        email,
        password,
        password_confirmation: passwordConfirmation,
    });

    return response.data;
}

export async function requestPasswordReset(email: string) {
    const response = await apiClient.post<{
        message: string;
        reset_token?: string;
        email?: string;
        reset_url?: string;
        dev_hint?: string;
    }>("/auth/forgot-password", {
        email,
    });

    return response.data;
}

export async function resetPasswordWithToken(email: string, token: string, password: string, passwordConfirmation: string) {
    const response = await apiClient.post<{ message: string }>("/auth/reset-password", {
        email,
        token,
        password,
        password_confirmation: passwordConfirmation,
    });

    return response.data;
}

export async function changePasswordWithBackend(currentPassword: string, password: string, passwordConfirmation: string) {
    const response = await apiClient.post<{ message: string }>("/auth/change-password", {
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation,
    });

    return response.data;
}

export async function fetchAuthenticatedUser() {
    const response = await apiClient.get<{ user: AuthenticatedUser }>("/auth/me");

    return response.data.user;
}

export async function logoutFromBackend() {
    await apiClient.post("/auth/logout");
}

/** Keepalive logout при затваряне на таб (без да чакаме отговора). */
export function logoutWithBeacon(): void {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return;
    }

    const xsrfToken = readCookie("XSRF-TOKEN");
    const headers: Record<string, string> = {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "Content-Type": "application/json",
    };

    if (xsrfToken) {
        headers["X-XSRF-TOKEN"] = xsrfToken;
    }

    const apiBaseUrl = import.meta.env.DEV
        ? "/api"
        : (import.meta.env.VITE_API_URL ?? "/api");

    void fetch(`${apiBaseUrl}/auth/logout`, {
        method: "POST",
        credentials: "include",
        keepalive: true,
        headers,
        body: "{}",
    }).catch(() => {
        // Затваряне на таб — няма как да покажем грешка.
    });
}

function readCookie(name: string): string | null {
    const encodedName = encodeURIComponent(name);
    const parts = document.cookie.split("; ");

    for (const part of parts) {
        if (!part.startsWith(`${encodedName}=`) && !part.startsWith(`${name}=`)) {
            continue;
        }

        const value = part.slice(part.indexOf("=") + 1);

        try {
            return decodeURIComponent(value);
        } catch {
            return value;
        }
    }

    return null;
}

export { ensureCsrfCookie };
